import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Edit2, Save, X, Camera, Mail, Phone, Shield, Settings, Eye, EyeOff } from 'lucide-react';
import { authAPI } from '../services/api';
import { validateImageFile, compressImage } from '../utils/avatarUtils';

export const UserProfile = ({ user, onUpdateUser }) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [passwordSuccessMessage, setPasswordSuccessMessage] = useState('');
  const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
  const [editedUser, setEditedUser] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    organization: user?.organization || '',
    municipality: user?.municipality || '',
    avatar: user?.avatar || null
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const fileInputRef = useRef(null);

  const handleInputChange = (field, value) => {
    setEditedUser(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAvatarClick = () => {
    if (isEditing) {
      fileInputRef.current?.click();
    }
  };

  const handleAvatarChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validation = validateImageFile(file);
    if (!validation.valid) {
      setErrorMessage(validation.error);
      return;
    }

    try {
      const compressedDataUrl = await compressImage(file);
      setEditedUser(prev => ({ ...prev, avatar: compressedDataUrl }));
      setErrorMessage('');
    } catch {
      setErrorMessage('Failed to process image. Please try another file.');
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');
    
    try {
      // Update user data with all edited fields including avatar
      const updatedUser = {
        ...user,
        firstName: editedUser.firstName,
        lastName: editedUser.lastName,
        email: editedUser.email,
        phone: editedUser.phone,
        organization: editedUser.organization,
        municipality: editedUser.municipality,
        avatar: editedUser.avatar
      };
      
      // Call the update function passed from parent
      await onUpdateUser(updatedUser);
      
      setIsEditing(false);
      setSuccessMessage('✅ Profile updated successfully! Your changes have been saved' + (editedUser.avatar ? ' including your profile picture.' : '.'));
      // Auto-hide success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      
      let errorMsg = 'Failed to update profile. ';
      
      if (error.message?.includes('too large') || error.message?.includes('size')) {
        errorMsg += 'The profile picture is too large. Please try a smaller image.';
      } else if (error.response?.data?.message) {
        errorMsg += error.response.data.message;
      } else {
        errorMsg += 'Please try again or contact support if the issue persists.';
      }
      
      setErrorMessage(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setEditedUser({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: user?.phone || '',
      organization: user?.organization || '',
      municipality: user?.municipality || '',
      avatar: user?.avatar || null
    });
    setIsEditing(false);
  };

  const handlePasswordInputChange = (field, value) => {
    setPasswordForm((prev) => ({ ...prev, [field]: value }));
    setPasswordErrorMessage('');
    setPasswordSuccessMessage('');
  };

  const handleChangePassword = async () => {
    setPasswordErrorMessage('');
    setPasswordSuccessMessage('');

    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordErrorMessage('All password fields are required.');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordErrorMessage('New password must be at least 6 characters long.');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordErrorMessage('New password and confirmation do not match.');
      return;
    }

    const userId = user?.id || user?.user_id;
    if (!userId) {
      setPasswordErrorMessage('User ID is missing. Please log in again.');
      return;
    }

    try {
      setIsChangingPassword(true);
      await authAPI.changePassword({
        userId,
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });

      setPasswordSuccessMessage('Password changed successfully.');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      setPasswordErrorMessage(error.message || 'Failed to change password.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const formatRole = (role) => {
    switch(role) {
      case 'admin': return 'Administrator';
      case 'user': return 'User';
      case 'processor': return 'Processor';
      case 'farmer': return 'Farmer';
      default: return role || 'User';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-green-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/home')}
              className="p-2 text-gray-600 hover:text-amber-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <h1 className="text-3xl font-bold text-gray-900">User Profile</h1>
          </div>
          
          <div className="flex items-center space-x-2">
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
              >
                <Edit2 className="w-4 h-4" />
                <span>Edit Profile</span>
              </button>
            ) : (
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleCancel}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  disabled={isLoading}
                >
                  <X className="w-4 h-4" />
                  <span>Cancel</span>
                </button>
                <button
                  onClick={handleSave}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  disabled={isLoading}
                >
                  <Save className="w-4 h-4" />
                  <span>{isLoading ? 'Saving...' : 'Save Changes'}</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Success/Error Notifications */}
        {successMessage && (
          <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg flex items-center">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            {successMessage}
          </div>
        )}

        {errorMessage && (
          <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg flex items-center">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {errorMessage}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg p-6">
              {/* Avatar Section */}
              <div className="text-center mb-6">
                <button 
                  type="button"
                  className={`relative inline-block ${isEditing ? 'cursor-pointer' : ''}`}
                  onClick={handleAvatarClick}
                  aria-label={isEditing ? 'Change avatar' : 'User avatar'}
                  tabIndex={isEditing ? 0 : -1}
                >
                  {(isEditing ? editedUser.avatar : user?.avatar) ? (
                    <img
                      src={isEditing ? editedUser.avatar : user?.avatar}
                      alt="Profile"
                      className="w-32 h-32 rounded-full object-cover mx-auto border-4 border-amber-200"
                    />
                  ) : (
                    <div className="w-32 h-32 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white text-3xl font-bold mx-auto border-4 border-amber-200">
                      {getInitials(editedUser.firstName || user?.firstName, editedUser.lastName || user?.lastName)}
                    </div>
                  )}
                  
                  {isEditing && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full">
                      <Camera className="w-8 h-8 text-white" />
                    </div>
                  )}
                </button>
                
                {isEditing && (
                  <p className="text-xs text-gray-500 mt-2">Click to upload (Max 5MB, will be resized to 400x400)</p>
                )}
                
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleAvatarChange}
                  accept="image/*"
                  className="hidden"
                />
                
                <h2 className="text-2xl font-bold text-gray-900 mt-4">
                  {`${editedUser.firstName || user?.firstName || ''} ${editedUser.lastName || user?.lastName || ''}`.trim() || 'User'}
                </h2>
                
                <div className="flex items-center justify-center mt-2">
                  <Shield className="w-4 h-4 text-amber-600 mr-2" />
                  <span className="text-amber-700 font-medium">
                    {formatRole(user?.role)}
                  </span>
                </div>
                
                {user?.username && (
                  <p className="text-gray-600 mt-1">@{user.username}</p>
                )}
              </div>

              {/* Quick Stats */}
              <div className="space-y-3">
                {user?.organization && (
                  <div className="flex items-center justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600">Organization</span>
                    <span className="font-medium">{user.organization}</span>
                  </div>
                )}
                
                {user?.municipality && (
                  <div className="flex items-center justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600">Municipality</span>
                    <span className="font-medium">{user.municipality}</span>
                  </div>
                )}
                
                {user?.role && (
                  <div className="flex items-center justify-between py-2">
                    <span className="text-gray-600">User Role</span>
                    <span className="font-medium">{formatRole(user.role)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Profile Details */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center mb-6">
                <Settings className="w-5 h-5 text-amber-600 mr-2" />
                <h3 className="text-xl font-semibold text-gray-900">Profile Information</h3>
              </div>

              <div className="space-y-6">
                {/* Personal Information */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        First Name
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editedUser.firstName}
                          onChange={(e) => handleInputChange('firstName', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          placeholder="Enter your first name"
                        />
                      ) : (
                        <div className="flex items-center px-3 py-2 bg-gray-50 rounded-lg">
                          <User className="w-4 h-4 text-gray-400 mr-2" />
                          <span>{user?.firstName || 'Not provided'}</span>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editedUser.lastName}
                          onChange={(e) => handleInputChange('lastName', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          placeholder="Enter your last name"
                        />
                      ) : (
                        <div className="flex items-center px-3 py-2 bg-gray-50 rounded-lg">
                          <User className="w-4 h-4 text-gray-400 mr-2" />
                          <span>{user?.lastName || 'Not provided'}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address
                      </label>
                      {isEditing ? (
                        <input
                          type="email"
                          value={editedUser.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          placeholder="Enter your email address"
                        />
                      ) : (
                        <div className="flex items-center px-3 py-2 bg-gray-50 rounded-lg">
                          <Mail className="w-4 h-4 text-gray-400 mr-2" />
                          <span>{user?.email || 'Not provided'}</span>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      {isEditing ? (
                        <input
                          type="tel"
                          value={editedUser.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          placeholder="Enter your phone number"
                        />
                      ) : (
                        <div className="flex items-center px-3 py-2 bg-gray-50 rounded-lg">
                          <Phone className="w-4 h-4 text-gray-400 mr-2" />
                          <span>{user?.phone || 'Not provided'}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Organization Information */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Organization Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Organization
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editedUser.organization}
                          onChange={(e) => handleInputChange('organization', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          placeholder="Enter your organization"
                        />
                      ) : (
                        <div className="flex items-center px-3 py-2 bg-gray-50 rounded-lg">
                          <span>{user?.organization || 'Not provided'}</span>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Municipality
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editedUser.municipality}
                          onChange={(e) => handleInputChange('municipality', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          placeholder="Enter your municipality"
                        />
                      ) : (
                        <div className="flex items-center px-3 py-2 bg-gray-50 rounded-lg">
                          <span>{user?.municipality || 'Not provided'}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-amber-800 mb-3">Change Password</h4>
                  <div className="grid grid-cols-1 gap-3">
                    <div className="relative">
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={passwordForm.currentPassword}
                        onChange={(e) => handlePasswordInputChange('currentPassword', e.target.value)}
                        className="w-full px-3 pr-10 py-2 border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        placeholder="Current password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                      >
                        {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <div className="relative">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        value={passwordForm.newPassword}
                        onChange={(e) => handlePasswordInputChange('newPassword', e.target.value)}
                        className="w-full px-3 pr-10 py-2 border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        placeholder="New password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                      >
                        {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={passwordForm.confirmPassword}
                        onChange={(e) => handlePasswordInputChange('confirmPassword', e.target.value)}
                        className="w-full px-3 pr-10 py-2 border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        placeholder="Confirm new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {passwordErrorMessage && (
                      <p className="text-sm text-red-600">{passwordErrorMessage}</p>
                    )}
                    {passwordSuccessMessage && (
                      <p className="text-sm text-green-700">{passwordSuccessMessage}</p>
                    )}
                    <div>
                      <button
                        type="button"
                        onClick={handleChangePassword}
                        disabled={isChangingPassword}
                        className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
                      >
                        {isChangingPassword ? 'Updating Password...' : 'Update Password'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};